import React, { useEffect, useRef } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Typography,
} from 'antd';
import type { InputRef } from 'antd';
import type { RuleObject } from 'antd/es/form';
import { SmbEndpointOption, SourceRoute } from '@/types';

const { Text } = Typography;

export const DEFAULT_ROUTE_ENDPOINT_ID = 'dev-dxgprivate';
export const DEFAULT_ROUTE_FOLDER = 'OUTGOING';

const MAX_ROUTE_EMAILS = 10;

export interface SourceRouteDraft {
  endpoint_id?: string;
  source_folder?: string;
  file_pattern?: string;
  active?: boolean;
  success_email?: string[];
  failure_email?: string[];
}

export function defaultFilePattern(vendorId: string): string {
  return `${vendorId.trim()}_*`;
}

export function defaultCreateRoute(vendorId = ''): SourceRoute {
  return {
    endpoint_id: DEFAULT_ROUTE_ENDPOINT_ID,
    source_folder: DEFAULT_ROUTE_FOLDER,
    file_pattern: defaultFilePattern(vendorId),
    active: true,
  };
}

export function blankSourceRoute(): SourceRoute {
  return {
    endpoint_id: '',
    source_folder: '',
    file_pattern: '',
    active: true,
  };
}

function normalizedEmails(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const addresses = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return addresses.length > 0 ? addresses : undefined;
}

export function normalizeSourceRoutes(
  routes: SourceRouteDraft[] | null | undefined,
): SourceRoute[] {
  if (!Array.isArray(routes)) {
    return [];
  }
  return routes.map((route) => {
    const normalized: SourceRoute = {
      endpoint_id: (route?.endpoint_id || '').trim(),
      source_folder: (route?.source_folder || '').trim(),
      file_pattern: (route?.file_pattern || '').trim(),
      active: route?.active === true,
    };
    const successEmail = normalizedEmails(route?.success_email);
    const failureEmail = normalizedEmails(route?.failure_email);
    if (successEmail) {
      normalized.success_email = successEmail;
    }
    if (failureEmail) {
      normalized.failure_email = failureEmail;
    }
    return normalized;
  });
}

function routeIdentity(route: SourceRoute): string {
  return [
    route.endpoint_id.trim().toLowerCase(),
    route.source_folder.trim().toLowerCase(),
    route.file_pattern.trim().toLowerCase(),
  ].join('\0');
}

function emailListsEqual(left: string[] | undefined, right: string[] | undefined): boolean {
  const first = left ?? [];
  const second = right ?? [];
  if (first.length !== second.length) {
    return false;
  }
  return first.every((address, index) => address.trim() === second[index].trim());
}

function routesEqual(left: SourceRoute[], right: SourceRoute[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((route, index) => {
    const other = right[index];
    return route.endpoint_id === other.endpoint_id
      && route.source_folder === other.source_folder
      && route.file_pattern === other.file_pattern
      && route.active === other.active
      && emailListsEqual(route.success_email, other.success_email)
      && emailListsEqual(route.failure_email, other.failure_email);
  });
}

/**
 * True when the editor still matches the routes GET would use.
 * A vendor with no route is shown as one blank row; leaving that row blank
 * is not a change, so an email-only save does not send source_routes.
 */
export function sourceRoutesUnchanged(
  drafts: SourceRouteDraft[] | null | undefined,
  baseline: SourceRoute[],
): boolean {
  const normalized = normalizeSourceRoutes(drafts);
  if (
    baseline.length === 0
    && normalized.length === 1
    && normalized[0].endpoint_id === ''
    && normalized[0].source_folder === ''
    && normalized[0].file_pattern === ''
    && normalized[0].active === true
    && emailListsEqual(normalized[0].success_email, undefined)
    && emailListsEqual(normalized[0].failure_email, undefined)
  ) {
    return true;
  }
  return routesEqual(normalized, baseline);
}

export function buildSourceEndpointOptions(
  endpoints: SmbEndpointOption[],
  currentIds: Array<string | null | undefined>,
): { value: string; label: string }[] {
  const options = endpoints.map((endpoint) => ({
    value: endpoint.endpoint_id,
    label: endpoint.display_name || endpoint.endpoint_id,
  }));
  const seen = new Set(options.map((option) => option.value));
  const missing: { value: string; label: string }[] = [];
  currentIds.forEach((id) => {
    const trimmed = (id || '').trim();
    if (!trimmed || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    missing.push({ value: trimmed, label: `${trimmed} (current)` });
  });
  return [...missing, ...options];
}

function routeLabel(index: number): string {
  return `source_routes[${index}]`;
}

function duplicateOf(routes: SourceRoute[], index: number): number | null {
  const seen = new Map<string, number>();
  for (let cursor = 0; cursor < routes.length; cursor += 1) {
    const key = routeIdentity(routes[cursor]);
    const prior = seen.get(key);
    if (prior !== undefined) {
      if (cursor === index) {
        return prior;
      }
    } else {
      seen.set(key, cursor);
    }
  }
  return null;
}

function validateEndpoint(
  baseline: SourceRoute[] | undefined,
  form: ReturnType<typeof Form.useFormInstance>,
  index: number,
  value: string,
): Promise<void> {
  const routes = normalizeSourceRoutes(form.getFieldValue('source_routes'));
  if (baseline && sourceRoutesUnchanged(routes, baseline)) {
    return Promise.resolve();
  }
  if (!(value || '').trim()) {
    return Promise.reject(new Error(`${routeLabel(index)} endpoint_id must not be empty`));
  }
  return Promise.resolve();
}

function validateFolder(
  baseline: SourceRoute[] | undefined,
  form: ReturnType<typeof Form.useFormInstance>,
  index: number,
  value: string,
): Promise<void> {
  const routes = normalizeSourceRoutes(form.getFieldValue('source_routes'));
  if (baseline && sourceRoutesUnchanged(routes, baseline)) {
    return Promise.resolve();
  }
  const folder = (value || '').trim();
  const label = routeLabel(index);
  if (!folder) {
    return Promise.reject(new Error(`${label} source_folder must not be empty`));
  }
  if (folder.startsWith('\\')) {
    return Promise.reject(new Error(`${label} source_folder must not start with \\`));
  }
  if (folder.includes('..')) {
    return Promise.reject(new Error(`${label} source_folder must not contain ..`));
  }
  return Promise.resolve();
}

function validatePattern(
  baseline: SourceRoute[] | undefined,
  form: ReturnType<typeof Form.useFormInstance>,
  index: number,
  value: string,
): Promise<void> {
  const routes = normalizeSourceRoutes(form.getFieldValue('source_routes'));
  if (baseline && sourceRoutesUnchanged(routes, baseline)) {
    return Promise.resolve();
  }
  const pattern = (value || '').trim();
  const label = routeLabel(index);
  if (!pattern) {
    return Promise.reject(new Error(`${label} file_pattern must not be empty`));
  }
  if (pattern.includes('\\') || pattern.includes('/')) {
    return Promise.reject(new Error(`${label} file_pattern must not contain \\ or /`));
  }
  const prior = duplicateOf(routes, index);
  if (prior !== null) {
    return Promise.reject(new Error(`${label} duplicates ${routeLabel(prior)}`));
  }
  return Promise.resolve();
}

function singleEmailError(value: string): string | null {
  if (/\s/u.test(value) || value.includes(',') || value.includes(';')) {
    return 'must be a single email address';
  }
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) {
    return 'must be a single email address';
  }
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!local || !domain || !domain.includes('.')) {
    return 'must be a single email address';
  }
  return null;
}

function validateAddressList(
  baseline: SourceRoute[] | undefined,
  form: ReturnType<typeof Form.useFormInstance>,
  _index: number,
  _field: 'success_email' | 'failure_email',
  value: unknown,
): Promise<void> {
  const routes = normalizeSourceRoutes(form.getFieldValue('source_routes'));
  if (baseline && sourceRoutesUnchanged(routes, baseline)) {
    return Promise.resolve();
  }
  if (value == null || (Array.isArray(value) && value.length === 0)) {
    return Promise.resolve();
  }
  if (!Array.isArray(value)) {
    return Promise.reject(new Error('Enter one address per entry.'));
  }
  const errors: string[] = [];
  if (value.length > MAX_ROUTE_EMAILS) {
    errors.push('Add at most 10 addresses per field.');
  }
  const seen = new Map<string, number>();
  value.forEach((item, itemIndex) => {
    if (typeof item !== 'string') {
      errors.push('Enter one address per entry.');
      return;
    }
    const text = item.trim();
    if (!text) {
      errors.push('Remove the empty entry.');
      return;
    }
    if (singleEmailError(text)) {
      errors.push(`"${text}" is not a valid email address. Enter one address per entry.`);
      return;
    }
    const key = text.toLowerCase();
    if (seen.has(key)) {
      errors.push(`"${text}" is listed more than once.`);
    } else {
      seen.set(key, itemIndex);
    }
  });
  if (errors.length > 0) {
    return Promise.reject(new Error(errors.join('; ')));
  }
  return Promise.resolve();
}

const PatternInput = React.forwardRef<
  InputRef,
  React.ComponentProps<typeof Input> & { onUserEdit?: () => void }
>(({ onUserEdit, onChange, ...rest }, ref) => (
  <Input
    ref={ref}
    placeholder="VENDOR_*"
    {...rest}
    onChange={(event) => {
      onUserEdit?.();
      onChange?.(event);
    }}
  />
));
PatternInput.displayName = 'PatternInput';

interface SourceRoutesEditorProps {
  options: { value: string; label: string }[];
  loading: boolean;
  unavailable: boolean;
  disabled?: boolean;
  /** Edit only. When the rows still match this list, field rules pass. */
  baseline?: SourceRoute[];
  /** Create only. Row 0's pattern follows Vendor ID until that pattern is edited. */
  followVendorId?: boolean;
}

const SourceRoutesEditor: React.FC<SourceRoutesEditorProps> = ({
  options,
  loading,
  unavailable,
  disabled = false,
  baseline,
  followVendorId = false,
}) => {
  const form = Form.useFormInstance();
  const vendorId = Form.useWatch('vendor_id', form);
  const patternTouchedRef = useRef(false);

  useEffect(() => {
    if (!followVendorId || patternTouchedRef.current) {
      return;
    }
    const routes = form.getFieldValue('source_routes');
    if (!Array.isArray(routes) || !routes[0]) {
      return;
    }
    const pattern = defaultFilePattern(typeof vendorId === 'string' ? vendorId : '');
    if (routes[0].file_pattern === pattern) {
      return;
    }
    form.setFieldValue(['source_routes', 0, 'file_pattern'], pattern);
  }, [followVendorId, form, vendorId]);

  return (
    <Form.List
      name="source_routes"
      rules={[
        {
          validator: async (_rule: RuleObject, value: SourceRouteDraft[] | undefined) => {
            if (baseline && sourceRoutesUnchanged(value, baseline)) {
              return;
            }
            if (!Array.isArray(value) || value.length === 0) {
              throw new Error('source_routes must not be empty');
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <div>
          <Text strong>Source routes</Text>
          {fields.map((field, index) => (
            <div
              key={field.key}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 16,
                marginTop: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Route {index + 1}</Text>
                <Button
                  danger
                  htmlType="button"
                  disabled={disabled || fields.length === 1}
                  onClick={() => {
                    if (followVendorId && field.name === 0) {
                      patternTouchedRef.current = true;
                    }
                    remove(field.name);
                  }}
                >
                  Remove
                </Button>
              </div>
              <Form.Item
                name={[field.name, 'endpoint_id']}
                label="Endpoint"
                dependencies={['source_routes']}
                rules={[{
                  validator: (_rule, value: string) => validateEndpoint(baseline, form, field.name, value),
                }]}
                extra={unavailable ? 'Source shares could not be loaded.' : undefined}
              >
                <Select
                  placeholder="Select an endpoint"
                  loading={loading}
                  disabled={disabled || unavailable}
                  options={options}
                />
              </Form.Item>
              <Form.Item
                name={[field.name, 'source_folder']}
                label="Source Folder"
                dependencies={['source_routes']}
                rules={[{
                  validator: (_rule, value: string) => validateFolder(baseline, form, field.name, value),
                }]}
              >
                <Input placeholder="OUTGOING" disabled={disabled} />
              </Form.Item>
              <Form.Item
                name={[field.name, 'file_pattern']}
                label="File Pattern"
                dependencies={['source_routes']}
                rules={[{
                  validator: (_rule, value: string) => validatePattern(baseline, form, field.name, value),
                }]}
              >
                <PatternInput
                  disabled={disabled}
                  onUserEdit={
                    followVendorId && field.name === 0
                      ? () => {
                        patternTouchedRef.current = true;
                      }
                      : undefined
                  }
                />
              </Form.Item>
              <Form.Item
                name={[field.name, 'active']}
                label="Active"
                valuePropName="checked"
              >
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item
                name={[field.name, 'success_email']}
                label="Success emails"
                dependencies={['source_routes']}
                extra="Leave blank to use the vendor's Default Success Email."
                rules={[{
                  validator: (_rule, value: unknown) => validateAddressList(
                    baseline,
                    form,
                    field.name,
                    'success_email',
                    value,
                  ),
                }]}
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Add an address"
                  disabled={disabled}
                  tokenSeparators={[',', ';']}
                  open={false}
                  suffixIcon={null}
                />
              </Form.Item>
              <Form.Item
                name={[field.name, 'failure_email']}
                label="Failure emails"
                dependencies={['source_routes']}
                extra="Leave blank to use the vendor's Default Failure Email."
                rules={[{
                  validator: (_rule, value: unknown) => validateAddressList(
                    baseline,
                    form,
                    field.name,
                    'failure_email',
                    value,
                  ),
                }]}
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Add an address"
                  disabled={disabled}
                  tokenSeparators={[',', ';']}
                  open={false}
                  suffixIcon={null}
                />
              </Form.Item>
            </div>
          ))}
          <Form.ErrorList errors={errors} />
          <Button
            htmlType="button"
            style={{ marginTop: 12 }}
            disabled={disabled}
            onClick={() => add(blankSourceRoute())}
          >
            Add route
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export default SourceRoutesEditor;
